/**
 * 地图文本标注防重叠管理器
 * 统一管理地图上所有 Leaflet tooltip（.leaflet-tooltip 元素）
 *
 * 核心原则：
 * 1. 碰撞检测统一用 getBoundingClientRect（container-point 屏幕坐标系）
 * 2. 视口外 tooltip 直接跳过（不做防重叠、不画引线）
 * 3. 引线 SVG 放在地图 pane 内（referenceLinePane），坐标统一换算成
 *    layer-point —— 平移时随地图 CSS transform 自动跟随标注；
 *    缩放时（本环境为瞬跳式，无 scale 动画）在 zoom 事件隐藏引线，
 *    zoomend 后重绘恢复
 * 4. 去重键用文本内容 + getBoundingClientRect 位置（而非 transform）
 *
 * 特性（组合方案 C+B+D）：
 *  - 去重：完全重叠去重（相同文本且屏幕位置一致的重复标注只保留一个）
 *  - D-缩放过滤：仅当实际发生重叠时，低缩放级别隐藏部分次要标注以消除重叠；
 *               若某级别下标注互不重叠，则一律不隐藏（即使低缩放）
 *  - C-最小位移：2 个标注重叠时按 Y 排序做最小垂直推开（位移量最小、最自然）
 *  - B-径向散开：3 个及以上标注重叠时，以簇中心为圆心按角度均匀散开
 *  - 引线：仅对实际发生位移的标注绘制虚线引线 + 锚点圆点
 *
 * 用法：
 *  const manager = createMapLabelManager(map, {
 *    collisionSelector: '.area-label', // 可选：仅对匹配该选择器的 tooltip 做防重叠
 *    drawReferenceLine: true            // 可选：位移后绘制引线
 *  })
 */
export function createMapLabelManager(map, options = {}) {
  const opts = {
    collisionSelector: null,
    drawReferenceLine: true,
    ...options
  }

  const manager = {
    map,
    layoutTimer: null,
    lineSvg: null,
    _layoutRunning: false,

    /**
     * 获取 tooltip 的屏幕矩形（container-point 坐标系）
     */
    getRect(el) {
      const rect = el.getBoundingClientRect()
      const containerRect = this.map.getContainer().getBoundingClientRect()
      return {
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top,
        w: rect.width,
        h: rect.height
      }
    },

    /**
     * 计算 tooltip 的去重键（文本 + 屏幕位置）
     */
    getDedupKey(el) {
      const text = (el.textContent || '').trim()
      const rect = this.getRect(el)
      return `${text}|${Math.round(rect.x)}|${Math.round(rect.y)}`
    },

    /**
     * 判断两个矩形是否相交
     */
    intersects(a, b) {
      return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
    },

    /**
     * 找出重叠簇：返回分组数组 [[idx...], ...]（并查集合并相交对）
     */
    findOverlapClusters(rects) {
      const n = rects.length
      const parent = Array.from({ length: n }, (_, i) => i)
      const find = x => (parent[x] === x ? x : (parent[x] = find(parent[x])))
      const union = (a, b) => {
        const ra = find(a); const rb = find(b)
        if (ra !== rb) parent[rb] = ra
      }
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          if (this.intersects(rects[i], rects[j])) union(i, j)
        }
      }
      const groups = new Map()
      for (let i = 0; i < n; i++) {
        const root = find(i)
        if (!groups.has(root)) groups.set(root, [])
        groups.get(root).push(i)
      }
      return Array.from(groups.values()).filter(g => g.length > 1)
    },

    /**
     * 设置 tooltip 位移：叠加 margin（Leaflet 只写 transform，二者互不冲突）
     */
    setOffset(el, offsetX, offsetY) {
      el.style.marginLeft = `${offsetX}px`
      el.style.marginTop = `${offsetY}px`
    },

    /**
     * 确保引线 SVG 元素存在（放在地图 pane 内，随地图 transform 变换）
     * 坐标统一用 layer-point：pane 平移/缩放时引线自动跟随标注，无需隐藏
     */
    ensureLineLayer() {
      if (this.lineSvg && this.lineSvg.isConnected) return
      const ns = 'http://www.w3.org/2000/svg'
      let pane = this.map.getPane('referenceLinePane')
      if (!pane) {
        pane = this.map.createPane('referenceLinePane')
        // 高于 markerPane(600)，低于 tooltipPane(650)，引线在标记之上、标注之下
        pane.style.zIndex = '640'
      }
      const svg = document.createElementNS(ns, 'svg')
      svg.setAttribute('data-label-lines', '1')
      svg.style.position = 'absolute'
      svg.style.overflow = 'visible'
      svg.style.top = '0'
      svg.style.left = '0'
      svg.style.pointerEvents = 'none'
      pane.appendChild(svg)
      this.lineSvg = svg
    },

    /**
     * 绘制引线（layer-point 坐标系，SVG 位于地图 pane 内）
     * fromX/fromY 是 tooltip 当前屏幕位置（含 margin 位移）
     * toX/toY 是锚点的屏幕位置（tooltip 未位移时的原始位置）
     * 入参为 container-point，绘制前统一换算为 layer-point
     */
    drawLines(items) {
      if (!opts.drawReferenceLine) return
      if (!items.length) {
        this.clearLines()
        return
      }
      this.ensureLineLayer()
      if (!this.lineSvg) return

      const ns = 'http://www.w3.org/2000/svg'
      const size = this.map.getSize()
      this.lineSvg.setAttribute('width', size.x)
      this.lineSvg.setAttribute('height', size.y)
      this.lineSvg.innerHTML = ''

      // container-point → layer-point 换算：两者之差即地图 pane 的 CSS transform 偏移
      const center = this.map.getCenter()
      const cp = this.map.latLngToContainerPoint(center)
      const lp = this.map.latLngToLayerPoint(center)
      const dx = cp.x - lp.x
      const dy = cp.y - lp.y

      items.forEach(item => {
        const fromX = item.fromX - dx
        const fromY = item.fromY - dy
        const toX = item.toX - dx
        const toY = item.toY - dy
        const line = document.createElementNS(ns, 'line')
        line.setAttribute('x1', fromX)
        line.setAttribute('y1', fromY)
        line.setAttribute('x2', toX)
        line.setAttribute('y2', toY)
        line.setAttribute('stroke', '#ff4d4f')
        line.setAttribute('stroke-width', '1')
        line.setAttribute('stroke-dasharray', '4,3')
        this.lineSvg.appendChild(line)
        const circle = document.createElementNS(ns, 'circle')
        circle.setAttribute('cx', toX)
        circle.setAttribute('cy', toY)
        circle.setAttribute('r', '3')
        circle.setAttribute('fill', '#ff4d4f')
        this.lineSvg.appendChild(circle)
      })
      // 重绘完成：恢复显示（缩放过程期间引线被隐藏）
      if (this.lineSvg.style.display === 'none') {
        this.lineSvg.style.display = ''
      }
    },

    /**
     * 清空引线
     */
    clearLines() {
      if (this.lineSvg) {
        this.lineSvg.innerHTML = ''
        // 缩放过程期间引线被隐藏，清空后同样恢复显示
        if (this.lineSvg.style.display === 'none') {
          this.lineSvg.style.display = ''
        }
      }
    },

    /**
     * 判断是否应参与防重叠计算
     */
    isCollisionTarget(el) {
      if (!opts.collisionSelector) return true
      return el.matches(opts.collisionSelector)
    },

    /**
     * 检查 tooltip 是否在视口附近（视口 2 倍范围内）
     */
    isNearViewport(el) {
      const rect = this.getRect(el)
      const size = this.map.getSize()
      const margin = Math.max(size.x, size.y) * 2
      return rect.x > -margin && rect.x < size.x + margin &&
             rect.y > -margin && rect.y < size.y + margin
    },

    /**
     * 执行布局：去重 + 防重叠（C+B+D）+ 引线
     */
    layout() {
      if (!this.map) return
      // 缩放动画期间跳过
      if (this.map._animatingZoom) {
        this.scheduleLayout()
        return
      }
      // 防止重入
      if (this._layoutRunning) {
        this.scheduleLayout()
        return
      }
      this._layoutRunning = true
      try {
        this._doLayout()
      } finally {
        this._layoutRunning = false
      }
    },

    _doLayout() {
      const container = this.map.getContainer()
      const tooltips = Array.from(container.querySelectorAll('.leaflet-tooltip'))
      if (!tooltips.length) {
        this.clearLines()
        return
      }

      // 只处理在视口附近的 tooltip（Leaflet 会将视口外图层的 tooltip 定位到
      // 巨大坐标值，getBoundingClientRect 会返回如 x:983056, y:-1768528 的坐标）
      const visibleTips = tooltips.filter(el => el && el.isConnected && this.isNearViewport(el))

      // ========== 1. 去重：相同文本 + 相同屏幕位置只保留一个 ==========
      const countByKey = new Map()
      visibleTips.forEach(el => {
        const key = this.getDedupKey(el)
        countByKey.set(key, (countByKey.get(key) || 0) + 1)
      })
      const dedupSet = new Set()
      const targets = []
      visibleTips.forEach(el => {
        const key = this.getDedupKey(el)
        if ((countByKey.get(key) || 0) > 1) {
          if (dedupSet.has(key)) {
            el.style.display = 'none'
            return
          }
          dedupSet.add(key)
        }
        el.style.display = ''
        if (this.isCollisionTarget(el)) targets.push(el)
      })
      if (!targets.length) {
        this.clearLines()
        return
      }

      // ========== 2. 复位 margin，读取原始矩形 ==========
      targets.forEach(el => {
        el.style.marginLeft = ''
        el.style.marginTop = ''
      })
      const rects = targets.map(el => this.getRect(el))

      // ========== 3. 碰撞检测 ==========
      const clusters = this.findOverlapClusters(rects)

      // ========== D-缩放级别过滤（仅当存在重叠时才启用） ==========
      const zoom = this.map.getZoom()
      const filteredOut = new Set()
      if (clusters.length > 0 && zoom < 14) {
        clusters.forEach(group => {
          const sorted = group
            .map(idx => ({ idx, y: rects[idx].y }))
            .sort((a, b) => a.y - b.y)
          const kept = []
          sorted.forEach(({ idx }) => {
            const r = rects[idx]
            const hit = kept.some(k => this.intersects(rects[k], r))
            if (!hit) {
              kept.push(idx)
            } else {
              filteredOut.add(targets[idx])
            }
          })
        })
      }

      const activeEls = targets.filter(el => !filteredOut.has(el))
      filteredOut.forEach(el => { el.style.display = 'none' })

      const activeRects = activeEls.map(el => this.getRect(el))
      const activeClusters = this.findOverlapClusters(activeRects)

      // ========== 4. C-最小位移 / B-径向散开 ==========
      const offsets = new Map()

      activeClusters.forEach(group => {
        const n = group.length
        if (n < 2) return
        const els = group.map(idx => activeEls[idx])
        const rs = group.map(idx => activeRects[idx])

        if (n === 2) {
          const [a, b] = rs[0].y <= rs[1].y ? [0, 1] : [1, 0]
          const overlap = rs[a].y + rs[a].h - rs[b].y
          const push = overlap > 0 ? Math.ceil(overlap) + 4 : 0
          if (push > 0) {
            offsets.set(els[a], { x: 0, y: -push })
            offsets.set(els[b], { x: 0, y: push })
          }
        } else {
          let cx = 0; let cy = 0
          rs.forEach(r => { cx += r.x + r.w / 2; cy += r.y + r.h / 2 })
          cx /= n; cy /= n
          const maxH = Math.max(...rs.map(r => r.h))
          const maxW = Math.max(...rs.map(r => r.w))
          const radius = Math.ceil(Math.max(40, maxH, maxW) * 1.1)
          els.forEach((el, i) => {
            const angle = (2 * Math.PI * i) / n - Math.PI / 2
            const fromX = rs[i].x + rs[i].w / 2
            const fromY = rs[i].y + rs[i].h / 2
            const toX = cx + radius * Math.cos(angle)
            const toY = cy + radius * Math.sin(angle)
            offsets.set(el, { x: Math.round(toX - fromX), y: Math.round(toY - fromY) })
          })
        }
      })

      // 应用位移
      activeEls.forEach(el => {
        const off = offsets.get(el)
        this.setOffset(el, off ? off.x : 0, off ? off.y : 0)
      })

      // ========== 5. 引线（仅对发生位移的标注） ==========
      if (opts.drawReferenceLine) {
        const lineItems = []
        activeEls.forEach(el => {
          const off = offsets.get(el)
          if (off && (off.x !== 0 || off.y !== 0)) {
            // fromX/fromY：tooltip 位移后的屏幕位置（含 margin）
            const rect = this.getRect(el)
            const fromX = rect.x + rect.w / 2
            const fromY = rect.y + rect.h / 2
            // toX/toY：tooltip 未位移时的原始位置（锚点位置）
            // 从屏幕位置减去 margin 得到原始位置
            const toX = rect.x + rect.w / 2 - off.x
            const toY = rect.y + rect.h / 2 - off.y
            lineItems.push({ fromX, fromY, toX, toY })
          }
        })
        this.drawLines(lineItems)
      } else {
        this.clearLines()
      }
    },

    /**
     * 防抖触发重新评估
     */
    scheduleLayout() {
      clearTimeout(this.layoutTimer)
      this.layoutTimer = setTimeout(() => this.layout(), 200)
    },

    /**
     * 销毁：移除所有事件监听与引线图层
     */
    destroy() {
      clearTimeout(this.layoutTimer)
      clearTimeout(this._zoomShowTimer)
      this.map.off('zoom')
      this.map.off('moveend')
      this.map.off('zoomend')
      this.map.off('layeradd')
      this.map.off('layerremove')
      if (this.lineSvg && this.lineSvg.isConnected) {
        this.lineSvg.remove()
      }
      this.lineSvg = null
    }
  }

  // 地图移动结束 / 缩放结束 / 新增或移除图层后重新评估
  // 注意：不监听 move（动画期间每帧触发），只在动画结束后收敛一次，
  //       避免高频重排导致页面卡死
  // 平移：引线 SVG 放在 pane 内，随地图 CSS transform 自动跟随标注，无需隐藏
  // 缩放：本环境缩放为瞬跳式（非 Leaflet scale 动画），pane 无法自动跟随，
  //       故在 zoom 事件首次触发时隐藏引线，zoomend 后重绘（drawLines 自动恢复显示）
  map.on('zoom', () => {
    if (manager.lineSvg && manager.lineSvg.style.display !== 'none') {
      manager.lineSvg.style.display = 'none'
    }
    // 兜底恢复：缩放结束后即使 zoomend 防抖被 AIS 推送等事件不断重置，
    // 也保证引线在缩放停止后恢复显示（连续滚轮时每次 zoom 都会重置本定时器）
    clearTimeout(manager._zoomShowTimer)
    manager._zoomShowTimer = setTimeout(() => {
      manager.layout()
    }, 300)
  })
  map.on('moveend', () => manager.scheduleLayout())
  // 缩放结束：保持隐藏并延迟重排（200ms 防抖），重绘后 drawLines/clearLines 自动恢复显示
  map.on('zoomend', () => manager.scheduleLayout())
  map.on('layeradd', () => manager.scheduleLayout())
  map.on('layerremove', () => manager.scheduleLayout())

  return manager
}
